import { env } from '@/config/env';
import { getItem, listItems } from '@/modules/inventory/services';
import { listReservations } from '@/modules/reservations/services/reservationRepository';
import type { InventoryItem } from '@/modules/inventory/types';
import type { Reservation } from '@/modules/reservations/types';
import { isValidDateRange } from '@/modules/reservations/utils/dateRange';
import {
  isItemEligibleForReservation,
  itemIneligibilityReason,
} from '@/modules/reservations/utils/itemEligibility';
import { listBlockingMaintenanceForAvailability } from '@/modules/maintenance/services/maintenanceRepository';
import type { MaintenanceTask } from '@/modules/maintenance/types/maintenance';
import { findOverlappingBlockingReservations } from '@/modules/reservations/utils/reservationFilters';
import {
  checkReservationAvailability,
  isWorkerReservationsEnabled,
  mapWorkerConflictsToReservations,
} from '@/services/api/reservations.api';

export type AvailabilityCheckParams = {
  inventoryItemIds: string[];
  startDate: string;
  endDate: string;
  excludeReservationId?: string;
};

export type MaintenanceAvailabilityConflict = {
  taskId: string;
  inventoryItemId: string;
  title: string;
  scheduledStart: string;
  scheduledEnd: string;
  type: MaintenanceTask['type'];
  status: MaintenanceTask['status'];
};

export type AvailabilityCheckResult = {
  available: boolean;
  conflicts: Reservation[];
  maintenanceConflicts?: MaintenanceAvailabilityConflict[];
  ineligibleItems: Array<{ itemId: string; reason: string }>;
  dateRangeValid: boolean;
};

export async function checkInventoryAvailability(
  params: AvailabilityCheckParams,
): Promise<AvailabilityCheckResult> {
  if (env.useWorkerReservations && isWorkerReservationsEnabled()) {
    const worker = await checkReservationAvailability(params);
    const maintenanceConflicts = worker.maintenanceConflicts?.map((m) => ({
      taskId: m.taskId,
      inventoryItemId: m.inventoryItemId,
      title: m.title,
      scheduledStart: m.scheduledStart,
      scheduledEnd: m.scheduledEnd,
      type: m.type as MaintenanceTask['type'],
      status: m.status as MaintenanceTask['status'],
    }));
    return {
      available: worker.available,
      conflicts: mapWorkerConflictsToReservations(worker.conflicts),
      ...(maintenanceConflicts?.length
        ? { maintenanceConflicts }
        : {}),
      ineligibleItems: worker.ineligibleItems,
      dateRangeValid: worker.dateRangeValid,
    };
  }

  const { inventoryItemIds, startDate, endDate, excludeReservationId } = params;

  const dateRangeValid = isValidDateRange(startDate, endDate);
  if (!dateRangeValid || inventoryItemIds.length === 0) {
    return {
      available: false,
      conflicts: [],
      ineligibleItems: [],
      dateRangeValid,
    };
  }

  const uniqueIds = [...new Set(inventoryItemIds)];
  const items: InventoryItem[] = [];
  const ineligibleItems: Array<{ itemId: string; reason: string }> = [];

  for (const itemId of uniqueIds) {
    const item = await getItem(itemId);
    if (!item) {
      ineligibleItems.push({ itemId, reason: 'Artículo no encontrado.' });
      continue;
    }
    if (!isItemEligibleForReservation(item)) {
      ineligibleItems.push({
        itemId,
        reason: itemIneligibilityReason(item) ?? 'No elegible.',
      });
      continue;
    }
    items.push(item);
  }

  if (ineligibleItems.length > 0) {
    return {
      available: false,
      conflicts: [],
      ineligibleItems,
      dateRangeValid: true,
    };
  }

  const reservations = await listReservations();
  const conflicts = findOverlappingBlockingReservations(
    reservations,
    uniqueIds,
    startDate,
    endDate,
    excludeReservationId,
  );

  const maintenanceBlocks = await listBlockingMaintenanceForAvailability(
    uniqueIds,
    startDate,
    endDate,
  );
  const maintenanceConflicts: MaintenanceAvailabilityConflict[] =
    maintenanceBlocks.map((t) => ({
      taskId: t.id,
      inventoryItemId: t.inventoryItemId,
      title: t.title,
      scheduledStart: t.scheduledStart,
      scheduledEnd: t.scheduledEnd,
      type: t.type,
      status: t.status,
    }));

  return {
    available: conflicts.length === 0 && maintenanceConflicts.length === 0,
    conflicts,
    ...(maintenanceConflicts.length > 0 ? { maintenanceConflicts } : {}),
    ineligibleItems: [],
    dateRangeValid: true,
  };
}

/** Ítems alquilables elegibles para selección en formulario (estado operativo). */
export async function listRentableSelectableItems(): Promise<InventoryItem[]> {
  const items = await listItems();
  return items.filter(isItemEligibleForReservation);
}
